// Features/Variant-Picker/Picker.ts
import { triggerEvent } from "Misc";
class VariantPicker extends CustomHTMLElement {
  masterSelector;
  pickers;
  product;
  async connectedCallback() {
    this.masterSelector = this.form.id;
    if (!this.masterSelector) {
      console.warn(`The variant selector for product with handle ${this.handle} is not linked to any product form.`);
      return;
    }
    this.pickers = Array.from(this.querySelectorAll(`[ data-selector-type ]`));
    this.product = await ProductLoader.load(this.handle);
    this.delegate.on("change", `[ name ^= option ]`, this.onOptionChanged.bind(this));
    this.masterSelector.addEventListener("change", this.onMasterSelectorChanged.bind(this));
    this.updateDisableSelectors();
    this.selectVariant(this.selectedVariant.id);
  }
  selectVariant(id) {
    const variant = this.getVariantById(id);
    if (!variant)
      return;
    if (!this.isVariantSelectable(variant)) {
      const selectable = this.getFirstMatchingAvailableOrSelectableVariant();
      if (!selectable)
        return;
      id = selectable.id;
    }
    if (this.selectedVariant?.id === id)
      return;
    this.masterSelector.value = String(id);
    const event = new Event("change", { bubbles: true });
    this.masterSelector.dispatchEvent(event);
    if (!this.isFeatured)
      this.updateURL(id);
    this.updateDisableSelectors();
    triggerEvent(this.masterSelector.form, "Variant:Changed", { variant: this.selectedVariant });
  }
  onOptionChanged(event) {
    const target = event.target;
    const variant = this.currentVariant;
    if (target.dataset.url && target.dataset.url !== this.dataset.url)
      window.location.href = target.dataset.url;
    else
      this.selectVariant(variant.id);
  }
  onMasterSelectorChanged() {
    const { options } = this.selectedVariant;
    options.forEach((value, index) => {
      const input = this.querySelector(`
                input[ name = 'option${index + 1}' ][ value = '${CSS.escape(value)}' ] ,
                select[ name = 'option${index + 1}' ]
            `);
      let triggerChangeEvent = false;
      if (input instanceof HTMLSelectElement) {
        triggerChangeEvent = input.value !== value;
        input.value = value;
      } else {
        triggerChangeEvent = !input.checked && input.value === value;
        input.checked = input.value === value;
      }
      if (triggerChangeEvent)
        input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }
  getVariantById(id) {
    return this.product.variants.find((variant) => variant.id === id);
  }
  isVariantSelectable(variant) {
    if (!variant)
      return false;
    if (variant.available)
      return true;
    if (this.hideSoldOut)
      return false;
    return true;
  }
  getFirstMatchingAvailableOrSelectableVariant() {
    const variants = this.product.variants;
    const available = variants.filter((variant) => variant.available || !this.hideSoldOut);
    const options = this.selectedOptions;
    let matchedVariant = null, slicedCount = 0;
    while (!matchedVariant && options.length > 0) {
      options.pop();
      slicedCount += 1;
      matchedVariant = available.find((variant) => variant.options.filter((option) => option !== "Default Title").slice(0, -slicedCount).every((value, index) => value === options[index]));
    }
    return matchedVariant;
  }
  updateURL(variantId) {
    const url = new URL(window.location.href);
    if (variantId)
      url.searchParams.set("variant", String(variantId));
    else
      url.searchParams.delete("variant");
    window.history.replaceState({
      path: url.toString()
    }, "", url.toString());
  }
  updateDisableSelectors() {
    const { selectedVariant } = this;
    if (!selectedVariant)
      return;
    const applyClassToSelector = (picker, valueIndex, available, hasAtLeastOneCombination) => {
      let selectorType = picker.dataset.selectorType;
      let cssSelector = "";
      switch (selectorType) {
        case "variant-image":
          cssSelector = `.variant-swatch`;
          break;
        case "dropdown":
          cssSelector = `.combo-box__option-item`;
          break;
        case "color":
          cssSelector = `.color-swatch`;
          break;
        case "block":
          cssSelector = `.block-swatch`;
          break;
      }
      cssSelector += `:nth-child(${valueIndex + 1})`;
      const element = picker.querySelector(cssSelector);
      if (!element)
        return;
      const sameProduct = element.dataset.url === this.dataset.url;
      if (!sameProduct)
        return;
      element.toggleAttribute("hidden", !hasAtLeastOneCombination);
      if (this.hideSoldOut)
        element.toggleAttribute("hidden", !available);
      else
        element.classList.toggle("is-disabled", !available);
    };
    const { variants, pickers, product } = this;
    for (let index = 0;index < pickers.length; index++) {
      const options = product.options[index].values;
      const picker = pickers[index];
      options.forEach((option, optionIndex) => {
        const filtered = variants.filter((variant) => {
          for (let i = 0;i < index; i++)
            if (variant.options[i] !== selectedVariant.options[i])
              return false;
          return true;
        });
        const hasCombination = filtered.some((variant) => variant.options[index] === option);
        const hasAvailable = filtered.filter((variant) => variant.available).some((variant) => variant.options[index] === option);
        applyClassToSelector(picker, optionIndex, hasAvailable, hasCombination);
      });
    }
  }
  get hideSoldOut() {
    return this.dataset.hideSoldOut === "true";
  }
  get isFeatured() {
    return this.dataset.isFeatured === "true";
  }
  get handle() {
    return this.dataset.handle;
  }
  get formId() {
    return this.dataset.formId;
  }
  get form() {
    return document.getElementById(this.formId);
  }
  get selectedVariant() {
    const variantId = this.masterSelector.value;
    if (variantId)
      return this.getVariantById(parseInt(variantId));
    return this.currentVariant;
  }
  get variants() {
    return this.product.variants;
  }
  get currentVariant() {
    const options = this.selectedOptions;
    return this.variants.find((variant) => variant.options.every((value, index) => {
      if (!options[index])
        return true;
      return value === options[index];
    }));
  }
  get selectedOptions() {
    const elements = this.querySelectorAll(`
            select[ name ^= option ] ,
            input[ name ^= option ]:checked
        `);
    return Array.from(elements).map((option) => option.value);
  }
}

// Features/Variant-Picker/mod.ts
window.customElements.define("variant-picker", VariantPicker);
